import { useEffect, useState } from 'react';
import api from '../../services/api';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { Id } from '../ui/Mono';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import { useToast } from '../ui/Toast';
import { BuildLocationStatusOptions, FormatBayWidth, ParseDecimalInput } from './locationFields';
import { useWarehouseLabels } from './warehouseLabels';

function buildFormFromLocation(location) {
    return {
        status: location?.status ?? 'ACTIVE',
        maxWeightKg: location?.max_weight_kg === null || location?.max_weight_kg === undefined
            ? ''
            : String(location.max_weight_kg),
        sector: location?.sector ?? '',
        notes: location?.notes ?? '',
    };
}

/**
 * Edição individual de uma localização (PATCH /warehouse/locations/:id):
 * status (Ativa/Bloqueada/Manutenção), peso máximo, setor e observações.
 * BLOCKED não é exclusão — é indisponibilidade (ex.: A5 até chegar a
 * empilhadeira maior). SEM DELETE, como todo o módulo.
 *
 * r3: a LARGURA DO VÃO saiu daqui — ela é do BLOCO (wms_blocks; o DTO da
 * posição só a espelha) e se ajusta pela Edição em massa. `rack_kind` morreu.
 */
const LocationEditModal = ({ location, onClose, onSaved }) => {
    const toast = useToast();
    const { label } = useWarehouseLabels();
    const [form, setForm] = useState(() => buildFormFromLocation(location));
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);

    const isOpen = Boolean(location);

    useEffect(() => {
        if (location) {
            setForm(buildFormFromLocation(location));
            setFormError(null);
        }
    }, [location]);

    const closeModal = () => {
        if (!saving) {
            onClose();
        }
    };

    const setField = (name, value) => {
        setForm((previous) => ({ ...previous, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const maxWeightKg = ParseDecimalInput(form.maxWeightKg);
        if (Number.isNaN(maxWeightKg) || (maxWeightKg !== null && maxWeightKg <= 0)) {
            setFormError({ field: 'maxWeightKg', message: 'Informe um número maior que zero (ou deixe vazio).' });
            return;
        }

        // Campo vazio → null (limpa o valor no banco) — o que está na tela é o
        // que fica salvo. SEM bay_width: largura é do bloco (bulk v2).
        const patch = {
            status: form.status,
            max_weight_kg: maxWeightKg,
            sector: form.sector.trim() || null,
            notes: form.notes.trim() || null,
        };

        setSaving(true);
        setFormError(null);
        try {
            await api.updateWarehouseLocation(location.id, patch);
            toast.success('Localização atualizada');
            onSaved?.();
            onClose();
        } catch (error) {
            if (error?.code === 'NETWORK_ERROR') {
                setFormError({ message: label('wms.collector.error_network') });
            } else {
                setFormError({ message: error?.message || 'Erro ao salvar' });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={closeModal} title={`${label('wms.common.edit')} — Localização`} size="md">
            {location ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2 rounded-lg bg-mono-50 p-3 dark:bg-mono-900">
                        <div className="flex flex-wrap items-center gap-2">
                            <Id value={location.code} className="text-base font-medium" />
                            <Badge>{label(`wms.location_type.${location.type}`)}</Badge>
                            {FormatBayWidth(location.bay_width) ? (
                                <Badge variant="neutral">
                                    {`${label('wms.field.bay_width')}: ${FormatBayWidth(location.bay_width)}`}
                                </Badge>
                            ) : null}
                        </div>
                        {FormatBayWidth(location.bay_width) ? (
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.edit.bay_width_readonly')}
                            </p>
                        ) : null}
                    </div>

                    <Select
                        label={label('wms.field.status')}
                        value={form.status}
                        onChange={(event) => setField('status', event.target.value)}
                        disabled={saving}
                    >
                        {BuildLocationStatusOptions(label).map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>

                    <Input
                        label={label('wms.field.max_weight_kg')}
                        inputMode="decimal"
                        placeholder="ex.: 1000"
                        value={form.maxWeightKg}
                        onChange={(event) => setField('maxWeightKg', event.target.value)}
                        errorMessage={formError?.field === 'maxWeightKg' ? formError.message : undefined}
                        disabled={saving}
                        autoComplete="off"
                    />

                    <Input
                        label={label('wms.field.sector')}
                        value={form.sector}
                        onChange={(event) => setField('sector', event.target.value)}
                        disabled={saving}
                        autoComplete="off"
                    />

                    <Textarea
                        label={label('wms.field.notes')}
                        rows={3}
                        value={form.notes}
                        onChange={(event) => setField('notes', event.target.value)}
                        disabled={saving}
                    />

                    {formError && !formError.field ? (
                        <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                            {formError.message}
                        </p>
                    ) : null}

                    <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                        <Button type="submit" loading={saving} className="flex-1">
                            {label('wms.common.save')}
                        </Button>
                        <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                            {label('wms.common.cancel')}
                        </Button>
                    </div>
                </form>
            ) : null}
        </Modal>
    );
};

export default LocationEditModal;
